(() => {
  const initialize = () => {
    const drawer = document.getElementById('HpSearchDrawer');
    if (!drawer || drawer.dataset.hpSearchReady === 'true') return;

    const panel = drawer.querySelector('.hp-search-drawer__panel');
    const input = drawer.querySelector('[data-hp-search-input]');
    const form = drawer.querySelector('[data-hp-search-form]');
    const state = drawer.querySelector('[data-hp-search-state]');
    const status = drawer.querySelector('[data-hp-search-status]');
    const summary = drawer.querySelector('[data-hp-search-summary]');
    const summaryCount = drawer.querySelector('[data-hp-search-count]');
    const summaryQuery = drawer.querySelector('[data-hp-search-query]');
    const emptyState = drawer.querySelector('[data-hp-search-empty]');
    const message = drawer.querySelector('[data-hp-search-message]');
    const results = drawer.querySelector('[data-hp-search-results]');
    const viewAll = drawer.querySelector('[data-hp-search-view-all]');
    const viewAllLabel = drawer.querySelector('[data-hp-search-view-all-label]');
    const closeButtons = drawer.querySelectorAll('[data-hp-search-close]');
    const openButtons = document.querySelectorAll('[data-hp-search-open]');
    const currency = drawer.dataset.currency || 'SGD';
    const searchUrl = drawer.dataset.searchUrl || '/search';
    const minimumCharacters = 2;
    const previewLimit = 10;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const compactDrawer = window.matchMedia('(max-width: 1024px)');
    let debounceTimer = 0;
    let closeTimer = 0;
    let openFrame = 0;
    let activeRequest = null;
    let requestSequence = 0;
    let lastTrigger = null;
    let lockedScrollY = 0;
    let lockUsesFixedBody = true;
    let keyboardMode = false;
    let resultsScrollbarFrame = 0;

    document.addEventListener('pointerdown', () => {
      keyboardMode = false;
    }, { capture: true, passive: true });

    document.addEventListener('keydown', () => {
      keyboardMode = true;
    }, { capture: true });

    drawer.dataset.hpSearchReady = 'true';

    const isOpen = () => drawer.getAttribute('aria-hidden') === 'false';
    const pageRequiresFlowPreservingScrollLock = () => (
      document.querySelector('[data-hp-flow-scroll-lock]') !== null
    );

    const fullResultsUrl = (query) => {
      const destination = new URL(searchUrl, window.location.origin);
      destination.searchParams.set('type', 'product');
      destination.searchParams.set('q', query);
      return destination;
    };

    const updateViewAllLabel = (query, totalCount = null) => {
      const label = totalCount > 1
        ? `View all ${totalCount} products`
        : totalCount === 1 ? 'View 1 product'
          : totalCount === 0 ? 'View search results' : 'View all products';
      if (viewAllLabel) viewAllLabel.textContent = label;
      viewAll?.setAttribute('aria-label', `${label} for ${query}`);
    };

    const formatMoney = (value) => {
      const amount = Number(value || 0);
      if (!Number.isFinite(amount)) return '';
      if (currency === 'SGD') return `$${amount.toFixed(2)} SGD`;

      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency,
        }).format(amount);
      } catch (_error) {
        return `${amount.toFixed(2)} ${currency}`;
      }
    };

    const setBusy = (busy) => {
      state?.setAttribute('aria-busy', busy ? 'true' : 'false');
      input?.setAttribute('aria-busy', busy ? 'true' : 'false');
    };

    const updateResultsScrollbar = () => {
      resultsScrollbarFrame = 0;
      if (!state || !results || results.hidden || !drawer.hasAttribute('data-hp-search-results-visible')) {
        drawer.removeAttribute('data-hp-search-overflow');
        return;
      }

      const initialScrollRange = Math.max(0, results.scrollHeight - results.clientHeight);
      if (initialScrollRange <= 1 || results.clientHeight <= 0) {
        drawer.removeAttribute('data-hp-search-overflow');
        state.style.removeProperty('--hp-search-scroll-thumb-top');
        state.style.removeProperty('--hp-search-scroll-thumb-height');
        return;
      }

      drawer.setAttribute('data-hp-search-overflow', '');
      const scrollRange = Math.max(1, results.scrollHeight - results.clientHeight);
      const stateRect = state.getBoundingClientRect();
      const resultsRect = results.getBoundingClientRect();
      const trackTop = Math.max(0, resultsRect.top - stateRect.top);
      const trackHeight = Math.max(1, results.clientHeight);
      const thumbHeight = Math.max(42, Math.round(trackHeight * (results.clientHeight / results.scrollHeight)));
      const maxThumbTop = Math.max(0, trackHeight - thumbHeight);
      const thumbTop = Math.round(maxThumbTop * (results.scrollTop / scrollRange));

      state.style.setProperty('--hp-search-scroll-thumb-top', `${Math.round(trackTop + thumbTop)}px`);
      state.style.setProperty('--hp-search-scroll-thumb-height', `${Math.min(trackHeight, thumbHeight)}px`);
    };

    const scheduleResultsScrollbarUpdate = () => {
      if (resultsScrollbarFrame) return;
      resultsScrollbarFrame = window.requestAnimationFrame(updateResultsScrollbar);
    };

    const setStatus = (text) => {
      if (status) status.textContent = text;
    };

    const lockPage = () => {
      lockedScrollY = window.scrollY || document.documentElement.scrollTop || 0;
      lockUsesFixedBody = !pageRequiresFlowPreservingScrollLock();
      const scrollbarComp = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
      // Preserve document flow, not visual height: fixed/absolute header groups overlay the page.
      const headerGroup = document.getElementById('header-group');
      const headerGroupPosition = headerGroup ? window.getComputedStyle(headerGroup).position : '';
      const headerFlowSpace =
        headerGroup && headerGroupPosition !== 'fixed' && headerGroupPosition !== 'absolute'
          ? headerGroup.getBoundingClientRect().height
          : 0;
      document.body.style.setProperty('--scrollbar-comp', `${scrollbarComp}px`);
      if (lockUsesFixedBody) {
        document.body.style.setProperty('--scroll-lock-top', `-${lockedScrollY}px`);
        document.body.style.setProperty('--scroll-lock-header-space', `${headerFlowSpace}px`);
      } else {
        document.body.style.removeProperty('--scroll-lock-top');
        document.body.style.removeProperty('--scroll-lock-header-space');
      }
      document.documentElement.classList.add('is-scroll-locked');
      document.body.classList.add('is-scroll-locked', 'hp-search-drawer-open');
      document.body.classList.toggle('is-scroll-locked-fixed', lockUsesFixedBody);
    };

    const unlockPage = () => {
      const shouldRestoreFixedBodyScroll = lockUsesFixedBody;
      const previousScrollBehavior = document.documentElement.style.scrollBehavior;
      document.documentElement.style.scrollBehavior = 'auto';
      document.documentElement.classList.remove('is-scroll-locked');
      document.body.classList.remove('is-scroll-locked', 'is-scroll-locked-fixed', 'hp-search-drawer-open');
      document.body.style.removeProperty('--scroll-lock-top');
      document.body.style.removeProperty('--scrollbar-comp');
      document.body.style.removeProperty('--scroll-lock-header-space');
      if (shouldRestoreFixedBodyScroll) {
        window.scrollTo({ left: 0, top: lockedScrollY, behavior: 'auto' });
      }
      lockUsesFixedBody = true;
      document.documentElement.style.scrollBehavior = previousScrollBehavior;
    };

    const setMessage = (text) => {
      if (!message) return;
      drawer.removeAttribute('data-hp-search-results-visible');
      drawer.removeAttribute('data-hp-search-overflow');
      message.textContent = text;
      message.hidden = false;
      if (emptyState) emptyState.hidden = true;
      if (results) {
        results.hidden = true;
        results.replaceChildren();
      }
      setStatus(text);
    };

    const showEmptyState = () => {
      drawer.removeAttribute('data-hp-search-results-visible');
      drawer.removeAttribute('data-hp-search-overflow');
      setBusy(false);
      if (summary) summary.hidden = true;
      if (viewAll) {
        viewAll.hidden = true;
        viewAll.removeAttribute('href');
        viewAll.removeAttribute('aria-label');
      }
      if (emptyState) emptyState.hidden = false;
      if (message) message.hidden = true;
      if (results) {
        results.hidden = true;
        results.replaceChildren();
      }
      setStatus('Enter at least two characters to search products.');
    };

    const buildResult = (product) => {
      const link = document.createElement('a');
      link.className = 'hp-search-result';
      link.href = product.url || '#';

      const sourceImage = product.featured_image?.url || product.image;
      let media;
      if (sourceImage) {
        media = document.createElement('img');
        media.alt = '';
        media.loading = 'eager';
        media.width = 160;
        media.height = 160;
        try {
          const imageUrl = new URL(sourceImage, window.location.origin);
          imageUrl.searchParams.set('width', '160');
          media.src = imageUrl.toString();
        } catch (_error) {
          media.src = sourceImage;
        }
      } else {
        media = document.createElement('span');
        media.setAttribute('aria-hidden', 'true');
        media.textContent = 'HP';
      }
      media.className = `hp-search-result__image${sourceImage ? '' : ' hp-search-result__image--placeholder'}`;

      const copy = document.createElement('div');
      copy.className = 'hp-search-result__copy';

      const type = document.createElement('p');
      type.className = 'hp-search-result__type';
      type.textContent = product.type || product.vendor || 'Product';

      const title = document.createElement('p');
      title.className = 'hp-search-result__title';
      title.textContent = product.title || 'View product';

      const meta = document.createElement('p');
      meta.className = 'hp-search-result__meta';

      const price = document.createElement('span');
      price.textContent = formatMoney(product.price_min ?? product.price);

      const availability = document.createElement('span');
      availability.className = `hp-search-result__availability${product.available ? '' : ' is-unavailable'}`;
      availability.textContent = product.available ? 'In stock' : 'Sold out';

      meta.append(price, availability);
      copy.append(type, title, meta);

      const arrow = document.createElement('span');
      arrow.className = 'hp-search-result__arrow';
      arrow.setAttribute('aria-hidden', 'true');
      arrow.textContent = '→';

      link.append(media, copy, arrow);
      return link;
    };

    const renderProducts = (products, query) => {
      const productList = Array.isArray(products) ? products.slice(0, previewLimit) : [];
      if (summary) summary.hidden = false;
      if (summaryCount) summaryCount.textContent = 'Products';
      if (summaryQuery) summaryQuery.textContent = `for “${query}”`;
      if (emptyState) emptyState.hidden = true;
      if (message) message.hidden = true;

      if (!productList.length) {
        setMessage(`No products found for “${query}”. Try another search.`);
        return;
      }

      drawer.setAttribute('data-hp-search-results-visible', '');

      if (results) {
        results.style.setProperty('--hp-search-result-count', String(productList.length));
        results.replaceChildren(...productList.map(buildResult));
        if (viewAll) results.append(viewAll);
        results.hidden = false;
        results.scrollTop = 0;
      }
      scheduleResultsScrollbarUpdate();
      setStatus(`Showing ${productList.length} products for ${query}. View all results to search the full catalog.`);
    };

    const requestProducts = async (query) => {
      activeRequest?.abort();
      activeRequest = new AbortController();
      const requestId = ++requestSequence;
      setBusy(true);
      setMessage('Searching products…');

      try {
        const products = await window.HpProductSearch.search(query, { searchUrl, signal: activeRequest.signal });
        if (requestId !== requestSequence) return;
        renderProducts(products, query);
        const totalCount = products.length;
        const shownCount = Math.min(totalCount, previewLimit);
        if (viewAll) viewAll.hidden = totalCount <= shownCount;
        scheduleResultsScrollbarUpdate();
        if (summaryCount) summaryCount.textContent = `${totalCount} ${totalCount === 1 ? 'Product' : 'Products'}`;
        updateViewAllLabel(query, totalCount);
        if (!totalCount && !shownCount) {
          setMessage(`No products found for “${query}”. Try another search.`);
        } else {
          setStatus(totalCount <= shownCount
            ? `Showing all ${shownCount} ${shownCount === 1 ? 'product' : 'products'} for ${query}.`
            : `${totalCount} products found for ${query}. Showing ${shownCount} quick results.`);
        }
      } catch (error) {
        if (error?.name === 'AbortError' || requestId !== requestSequence) return;
        setBusy(false);
        setMessage('Search is temporarily unavailable. Press Search to view the full results page.');
        if (viewAll) {
          viewAll.hidden = false;
          message?.append(viewAll);
        }
      } finally {
        if (requestId === requestSequence) setBusy(false);
      }
    };

    const queueSearch = () => {
      window.clearTimeout(debounceTimer);
      requestSequence += 1;
      activeRequest?.abort();
      const query = input?.value.trim() || '';

      if (query.length < minimumCharacters) {
        showEmptyState();
        return;
      }

      if (viewAll) {
        viewAll.href = fullResultsUrl(query).toString();
        viewAll.hidden = true;
        updateViewAllLabel(query);
      }
      if (summary) summary.hidden = true;
      setBusy(true);
      setMessage('Searching products…');
      debounceTimer = window.setTimeout(() => requestProducts(query), 180);
    };

    const visibleOpenButton = () => Array.from(openButtons).find((button) => {
      const rect = button.getBoundingClientRect();
      const style = window.getComputedStyle(button);
      return rect.width > 1 && rect.height > 1 && style.visibility !== 'hidden' && style.display !== 'none';
    });

    const getFocusable = () => {
      if (!panel) return [];
      return Array.from(panel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )).filter((element) => {
        const rect = element.getBoundingClientRect();
        const style = window.getComputedStyle(element);
        return rect.width > 0 && rect.height > 0 && style.visibility !== 'hidden' && style.display !== 'none';
      });
    };

    const openDrawer = (trigger) => {
      if (isOpen()) {
        input?.focus({ preventScroll: true });
        return;
      }
      document.dispatchEvent(new CustomEvent('hp:search:opening'));
      window.clearTimeout(closeTimer);
      window.cancelAnimationFrame(openFrame);
      openFrame = 0;
      lastTrigger = trigger instanceof HTMLElement ? trigger : visibleOpenButton() || null;
      drawer.hidden = false;
      drawer.removeAttribute('inert');
      drawer.setAttribute('aria-hidden', 'false');
      drawer.classList.remove('is-closing');
      drawer.getBoundingClientRect();
      drawer.classList.add('is-open');
      openButtons.forEach((button) => button.setAttribute('aria-expanded', 'true'));
      lockPage();
      if (state?.getAttribute('aria-busy') === 'true') queueSearch();
      openFrame = window.requestAnimationFrame(() => {
        openFrame = 0;
        if (!isOpen()) return;
        input?.focus({ preventScroll: true });
      });
    };

    const closeDrawer = ({ restoreFocus = keyboardMode } = {}) => {
      if (!isOpen()) return;
      window.cancelAnimationFrame(openFrame);
      openFrame = 0;
      requestSequence += 1;
      activeRequest?.abort();
      window.clearTimeout(debounceTimer);
      drawer.setAttribute('aria-hidden', 'true');
      drawer.classList.remove('is-open');
      drawer.classList.add('is-closing');
      openButtons.forEach((button) => button.setAttribute('aria-expanded', 'false'));

      const focusTarget = lastTrigger?.isConnected && lastTrigger.getClientRects().length
        ? lastTrigger
        : visibleOpenButton();
      lastTrigger = null;
      if (restoreFocus && focusTarget && typeof focusTarget.focus === 'function') {
        focusTarget.focus({ preventScroll: true });
      } else if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }

      drawer.setAttribute('inert', '');
      unlockPage();

      const closeDelay = reduceMotion.matches || compactDrawer.matches ? 0 : 140;
      closeTimer = window.setTimeout(() => {
        drawer.classList.remove('is-open', 'is-closing');
        if (drawer.getAttribute('aria-hidden') === 'true') drawer.hidden = true;
      }, closeDelay);
    };

    openButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        openDrawer(button);
      });
    });

    closeButtons.forEach((button) => button.addEventListener('click', () => closeDrawer()));
    input?.addEventListener('input', queueSearch);
    results?.addEventListener('scroll', scheduleResultsScrollbarUpdate, { passive: true });
    window.addEventListener('resize', scheduleResultsScrollbarUpdate, { passive: true });
    document.addEventListener('hp:menu:opening', () => closeDrawer({ restoreFocus: false }), { capture: true });
    document.addEventListener('hp:cart:opening', () => closeDrawer({ restoreFocus: false }), { capture: true });
    document.addEventListener('hp:account:opening', () => closeDrawer({ restoreFocus: false }), { capture: true });

    form?.addEventListener('submit', (event) => {
      const query = input?.value.trim() || '';
      if (!query.length) {
        event.preventDefault();
        input?.focus();
        return;
      }

      event.preventDefault();
      window.location.assign(fullResultsUrl(query).toString());
    });

    window.addEventListener('keydown', (event) => {
      if (!isOpen()) return;

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        event.stopImmediatePropagation();
        input?.focus({ preventScroll: true });
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopImmediatePropagation();
        closeDrawer();
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = getFocusable();
      if (!focusable.length) {
        event.preventDefault();
        panel?.focus({ preventScroll: true });
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || !panel?.contains(document.activeElement))) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && (document.activeElement === last || !panel?.contains(document.activeElement))) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    }, { capture: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
